import React, { useEffect, useState } from "react";
import { ChevronRight, ChevronDown, Check, X as XIcon, Brain } from "lucide-react";

/**
 * Claude-style "thinking" for the research chat.
 *
 *   live   — while the bot works: a pulsing "Thinking · 6s" header, the current
 *            phase ("Cross-checking the numbers"), each lookup in desk language
 *            with a spinner that turns into a tick and its time, and the model's
 *            own interim note ("Let me check the DCF against the Street target").
 *   done   — collapsed under the answer as "Thought for 8.4s · 3 lookups ▸";
 *            click to see the steps again.
 *
 * Props: live: { events: [...] } | trace: { elapsed_s, steps: [{text, ok, ms}], notes } ; dense
 */

const KEYFRAMES = `
@keyframes faThinkPulse { 0%,100% { opacity: .35 } 50% { opacity: 1 } }
@keyframes faThinkSpin { to { transform: rotate(360deg) } }
@keyframes faThinkIn { from { opacity: 0; transform: translateY(2px) } to { opacity: 1; transform: none } }
`;
let injected = false;
const useKeyframes = () => {
    useEffect(() => {
        if (injected || typeof document === "undefined") return;
        const el = document.createElement("style");
        el.textContent = KEYFRAMES;
        document.head.appendChild(el);
        injected = true;
    }, []);
};

const fmtMs = (ms) => (ms == null ? "" : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`);

/** Fold the raw event stream into what the panel shows. */
export const foldEvents = (events = []) => {
    const steps = new Map();
    let head = "Reading the question";
    let note = "";
    let final = null;
    for (const ev of events) {
        if (ev.kind === "think") head = ev.text || head;
        else if (ev.kind === "note") note = ev.text || "";
        else if (ev.kind === "step") { steps.set(ev.id, { text: ev.text, done: false }); note = ""; }
        else if (ev.kind === "done") steps.set(ev.id, { ...(steps.get(ev.id) || { text: ev.text }), done: true, ok: ev.ok !== false, ms: ev.ms });
        else if (ev.kind === "final") final = ev;
    }
    return { head, note, steps: [...steps.values()], final };
};

const Row = ({ step, live }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, animation: "faThinkIn .25s ease-out" }}>
        <span style={{ width: 14, flexShrink: 0, marginTop: 2, display: "inline-flex", justifyContent: "center" }}>
            {!step.done && live ? (
                <span style={{
                    width: 10, height: 10, borderRadius: "50%", display: "inline-block",
                    border: "2px solid var(--color-border, #d1d5db)", borderTopColor: "var(--color-primary, #0096FF)",
                    animation: "faThinkSpin .8s linear infinite",
                }} />
            ) : step.ok === false ? (
                <XIcon size={13} color="var(--color-error, #dc2626)" />
            ) : (
                <Check size={13} color="var(--color-success, #16a34a)" />
            )}
        </span>
        <span style={{ flex: 1, color: step.done || !live ? "var(--color-text-secondary)" : "var(--color-text-primary)" }}>
            {step.text}
            {step.done && step.ms != null ? (
                <span style={{ color: "var(--color-text-muted)", marginLeft: 6, fontVariantNumeric: "tabular-nums" }}>{fmtMs(step.ms)}</span>
            ) : null}
        </span>
    </div>
);

const ThinkingTrace = ({ live, trace, dense }) => {
    useKeyframes();
    const [open, setOpen] = useState(false);
    const [tick, setTick] = useState(0);
    const [startedAt] = useState(() => Date.now());

    useEffect(() => {
        if (!live) return undefined;
        const id = setInterval(() => setTick((n) => n + 1), 500);
        return () => clearInterval(id);
    }, [live]);

    const box = {
        fontSize: dense ? 12 : 12.5, lineHeight: 1.5, margin: "2px 0 6px 2px", padding: "8px 10px",
        borderLeft: "2px solid var(--color-border, #e5e7eb)", display: "flex", flexDirection: "column", gap: 4,
    };

    if (live) {
        const f = foldEvents(live.events);
        const secs = Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) + tick * 0; // tick forces re-render
        return (
            <div style={box} aria-live="polite">
                <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--color-text-secondary)", fontWeight: 600 }}>
                    <Brain size={14} style={{ animation: "faThinkPulse 1.4s ease-in-out infinite" }} />
                    <span>Thinking</span>
                    <span style={{ color: "var(--color-text-muted)", fontWeight: 400, fontVariantNumeric: "tabular-nums" }}>· {secs}s</span>
                </div>
                <div style={{ color: "var(--color-text-muted)", fontStyle: "italic", animation: "faThinkPulse 2s ease-in-out infinite" }}>
                    {f.head}…
                </div>
                {f.steps.map((st, i) => <Row key={i} step={st} live />)}
                {f.note ? (
                    <div style={{ color: "var(--color-text-muted)", borderLeft: "2px solid var(--color-primary, #0096FF)", paddingLeft: 8, marginTop: 2 }}>
                        {f.note}
                    </div>
                ) : null}
            </div>
        );
    }

    if (!trace) return null;
    const n = (trace.steps || []).length;
    if (!n && !(trace.notes || []).length) {
        return trace.elapsed_s >= 2 ? (
            <div style={{ ...box, borderLeft: "none", padding: "0 2px", color: "var(--color-text-muted)" }}>Thought for {trace.elapsed_s}s</div>
        ) : null;
    }
    return (
        <div style={{ ...box, borderLeft: open ? box.borderLeft : "none", padding: open ? box.padding : "0 2px" }}>
            <button type="button" onClick={() => setOpen((o) => !o)}
                style={{ all: "unset", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, color: "var(--color-text-muted)" }}>
                {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                <span>Thought for {trace.elapsed_s}s{n ? ` · ${n} lookup${n === 1 ? "" : "s"}` : ""}</span>
            </button>
            {open && (
                <>
                    {(trace.steps || []).map((st, i) => <Row key={i} step={{ ...st, done: true }} />)}
                    {(trace.notes || []).map((t, i) => (
                        <div key={`n${i}`} style={{ color: "var(--color-text-muted)", borderLeft: "2px solid var(--color-border, #e5e7eb)", paddingLeft: 8 }}>{t}</div>
                    ))}
                </>
            )}
        </div>
    );
};

export default ThinkingTrace;
